import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTransportQueueHubSummary } from "../../service/transportQueueHub.api";
import {
  buildHubQueryParams,
  EMPTY_QUEUE_HUB_FILTERS,
  mapTransportQueueHubFreights,
} from "../../view/filas-transportadoras/transportQueueHub.mapper";
import {
  TRANSPORT_QUEUE_NOTIFICATIONS_UPDATED_EVENT,
  buildTransportadorasBadgeTooltip,
  isTransportQueueNotificationRead,
} from "../../components/notifications/transportQueueNotification.utils";

const POLL_INTERVAL_MS = 60000;

/**
 * Badge do menu Transportadoras: entradas não lidas na fila de fretes.
 */
export default function useTransportadorasQueueBadge(user, enabled = true) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [tooltipTitle, setTooltipTitle] = useState("");
  const pollRequestRef = useRef(0);

  const canPoll = Boolean(
    enabled && user?.uid && user?.permissoes?.filasTransportadoras?.isView,
  );

  const refreshCount = useCallback(async () => {
    if (!canPoll) {
      setUnreadCount(0);
      setTooltipTitle("");
      return;
    }

    const requestId = ++pollRequestRef.current;

    try {
      const data = await fetchTransportQueueHubSummary(
        buildHubQueryParams(
          { ...EMPTY_QUEUE_HUB_FILTERS, onlyWithQueue: true },
          user,
        ),
      );

      if (requestId !== pollRequestRef.current) return;

      const freights = mapTransportQueueHubFreights(data);
      const unreadEntries = [];

      freights.forEach((freight) => {
        (freight.queue || []).forEach((candidate) => {
          const idTransport = String(
            candidate.idCarrier || candidate.idTransport || "",
          ).trim();
          if (!idTransport) return;
          if (
            isTransportQueueNotificationRead(freight.freightId, idTransport)
          ) {
            return;
          }
          unreadEntries.push({
            freightId: freight.freightId,
            idTransport,
            transportName: candidate.nameCarrier || "Transportadora",
          });
        });
      });

      setUnreadCount(unreadEntries.length);
      setTooltipTitle(
        buildTransportadorasBadgeTooltip(
          unreadEntries.map((entry) => entry.transportName),
        ),
      );
    } catch (error) {
      console.error(
        "[useTransportadorasQueueBadge] Erro ao atualizar badge:",
        error,
      );
    }
  }, [canPoll, user]);

  useEffect(() => {
    if (!canPoll) {
      setUnreadCount(0);
      setTooltipTitle("");
      return undefined;
    }

    refreshCount();
    const intervalId = window.setInterval(refreshCount, POLL_INTERVAL_MS);

    const onUpdated = () => refreshCount();

    window.addEventListener(
      TRANSPORT_QUEUE_NOTIFICATIONS_UPDATED_EVENT,
      onUpdated,
    );
    window.addEventListener("focus", onUpdated);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(
        TRANSPORT_QUEUE_NOTIFICATIONS_UPDATED_EVENT,
        onUpdated,
      );
      window.removeEventListener("focus", onUpdated);
    };
  }, [canPoll, refreshCount]);

  return { unreadCount, tooltipTitle };
}
